import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Tab, Tabs, Form, Row, Col } from 'react-bootstrap';
import { reduxForm, getFormValues, Field } from 'redux-form';
import InternalContainer from '../../../layout/containers/Internal';
import { Box, Button } from '../../../../common/components';
import { get, has } from 'lodash';
import './styles.scss';
import {
  General,
  Company,
  Account,
  Indemnity,
  Declaration,
  Regulation,
  BrokerApUsers,
  BankAccounts,
} from '../../../broker/components/BrokerForm/components';

import brokerSteps from '../../../../helpers/brokerSteps';
import { compose } from 'redux';
import { InternalButtonBar } from '../../../layout/components';
import SelectMenu from '../../../../common/components/SelectMenu/SelectMenu';
import BrokerApplicationCommissions from '../BrokerApplicationsCommissions/BrokerApplicationCommissions';
import { getFormattedInsurers } from '../../../broker/helpers';
import { hasPermission } from '../../../auth/redux/authActions';
import BrokerApplicationEmails from '../BrokerApplicationEmails/BrokerApplicationEmails';

const FORM_NAME = 'BrokerApplication';

const GENERAL = 1;
const COMPANY = 2;
const ACCOUNT = 3;
const INDEMNITY = 4;
const BANK_ACCOUNTS = 5;
const REGULATION = 6;
const DECLARATION = 7;
const USERS = 8;
const INSURERS = 9;
const EMAILS = 10;

const Decline = ({ onDecline, isSubmitting }) => (
  <Button
    size="large"
    key={'ba-decline'}
    handleClick={onDecline}
    className="pull-right"
    label={'Decline'}
    bsStyle="primary"
    isLoading={isSubmitting}
  />
);

const Resend = ({ onResend, isSubmitting }) => (
  <Button
    size="large"
    key={'ba-resend'}
    handleClick={onResend}
    className="pull-right"
    label={'Resend Approval'}
    bsStyle="primary"
    isLoading={isSubmitting}
  />
);

const Approve = ({ onApprove, isSubmitting, isDisabled }) => (
  <Button
    size="large"
    key={'ba-approve'}
    handleClick={onApprove}
    className="ba-approve-btn pull-right"
    label={'Approve'}
    bsStyle="primary"
    disabled={isDisabled}
    isLoading={isSubmitting}
  />
);
const getButtons = (
  activeTab,
  status,
  handleDecline,
  isSubmitting,
  handleApprove,
  handleResendApproval,
  goNextTab,
  goPreviousTab,
  handleSave,
  isFormValid,
) => {
  const buttons = [];

  if (activeTab < INSURERS) {
    buttons.push({
      button: (
        <Button
          className="btn-action pull-right"
          bsStyle="primary"
          handleClick={goNextTab}
          label="Next"
        />
      ),
    });
  }
  buttons.push({
    permissions: ['broker.edit'],
    button: (
      <Button
        size="large"
        handleClick={handleSave}
        className="pull-right"
        label={'Save'}
        bsStyle="secondary"
        isLoading={isSubmitting}
      />
    ),
  });
  if (activeTab > GENERAL) {
    buttons.push({
      button: (
        <Button
          className="btn-action-back"
          bsStyle="secondary"
          handleClick={goPreviousTab}
          label="Back"
        />
      ),
    });
  }

  if (activeTab > DECLARATION) {
    buttons.push({
      permissions: ['broker.edit'],
      button: (
        <Approve
          onApprove={handleApprove}
          isSubmitting={isSubmitting}
          isDisabled={!isFormValid}
        />
      ),
    });
  }

  if (status === 'applied') {
    buttons.push({
      permissions: ['broker.edit'],
      button: <Decline onDecline={handleDecline} isSubmitting={isSubmitting} />,
    });
  }

  if (status === 'approved') {
    buttons.push(
      {
        permissions: ['broker.edit'],
        button: <Decline onDecline={handleDecline} isSubmitting={isSubmitting} />,
      },
      {
        permissions: ['broker.edit'],
        button: <Resend onResend={handleResendApproval} isSubmitting={isSubmitting} />,
      },
    );
  }

  return buttons;
};

const getAssignedCommissions = (commissions) => {
  if (!commissions) {
    return [];
  }
  return commissions.reduce((carry, productCommissions) => {
    const enabled = productCommissions.rates.filter((rate) => rate.enabled);

    enabled.forEach((comm) => {
      carry.push({
        product_id: productCommissions.product,
        insurer: comm.insurer,
        rate: comm.rate,
      });
    });

    return carry;
  }, []);
};

const BrokerApplicationForm = ({
  onClearExistingOrganisation,
  onSave,
  onUpdate,
  onSaveAndApproveBrokerApplication,
  onResendBrokerApplication,
  onDeclineBrokerApplication,
  handleSubmit,
  availableProducts,
  formValues,
  dispatch,
  change,
  isSubmitting,
}) => {
  const [activeTab, setActiveTab] = useState(1);

  useEffect(
    () => {
      return () => {
        onClearExistingOrganisation();
      };
    },
    [onClearExistingOrganisation],
  );

  const handleTab = (key) => {
    setActiveTab(key);
  };

  const goNextTab = () => {
    setActiveTab(activeTab + 1);
  };

  const goPreviousTab = () => {
    setActiveTab(activeTab - 1);
  };

  const handleSave = () => {
    return Promise.resolve(
      dispatch(change('data.attributes.complete', formValidForApproval(formValues))),
    ).then(() => {
      handleSubmitBrokerApplication();
    });
  };

  const handleSubmitBrokerApplication = () => {
    if (!get(formValues, 'data.id')) {
      return onSave(formValues, true);
    } else {
      return Promise.resolve(onUpdate(formValues)).then((resp) => {
        dispatch(change('data', resp.data));
      });
    }
  };

  const handleApprove = () => {
    if (formValidForApproval(formValues)) {
      onSaveAndApproveBrokerApplication(formValues);
    }
  };

  const handleDecline = () => {
    onDeclineBrokerApplication(formValues);
  };

  const handleResendApproval = () => {
    onResendBrokerApplication(formValues);
  };

  const buttons = getButtons(
    activeTab,
    get(formValues, 'data.attributes.status'),
    handleDecline,
    isSubmitting,
    handleApprove,
    handleResendApproval,
    goNextTab,
    goPreviousTab,
    handleSave,
    formValidForApproval(formValues),
  );

  const brokerApplicationId = formValues?.data?.id;

  const CompanyDateIsValidDate = () => {
    if (
      get(formValues, 'data.attributes.metadata.date_business_established') === undefined
    ) {
      return true;
    }
    const dateEntered = new Date(
      get(formValues, 'data.attributes.metadata.date_business_established'),
    );
    return dateEntered instanceof Date && !isNaN(dateEntered);
  };
  const declarationDateIsValidDate = () => {
    if (
      get(formValues, 'data.attributes.metadata.declaration.date') === undefined ||
      get(formValues, 'data.attributes.metadata.declaration.date') === ''
    ) {
      return true;
    }
    const dateEntered = new Date(
      get(formValues, 'data.attributes.metadata.declaration.date'),
    );
    return dateEntered instanceof Date && !isNaN(dateEntered);
  };
  const indemnityDateIsValidDate = () => {
    if (
      get(formValues, 'data.attributes.metadata.insurer.expiry_date') === undefined ||
      get(formValues, 'data.attributes.metadata.insurer.expiry_date') === ''
    ) {
      return true;
    }
    const dateEntered = new Date(
      get(formValues, 'data.attributes.metadata.insurer.expiry_date'),
    );
    return dateEntered instanceof Date && !isNaN(dateEntered);
  };

  return (
    <InternalContainer title={'Broker Application'}>
      <Form onSubmit={handleSubmit}>
        <div className="tab-container">
          <Tabs
            activeKey={activeTab}
            id="broker-application"
            unmountOnExit={true}
            onSelect={handleTab}
          >
            <Tab eventKey={GENERAL} title={'General'}>
              <General content={brokerSteps(0)} form={FORM_NAME} />
            </Tab>
            <Tab eventKey={COMPANY} title={'Company'}>
              <Company
                content={brokerSteps(1)}
                form={FORM_NAME}
                isValidDate={CompanyDateIsValidDate}
              />
            </Tab>
            <Tab eventKey={ACCOUNT} title={'Account'}>
              <Account content={brokerSteps(2)} form={FORM_NAME} />
            </Tab>
            <Tab eventKey={INDEMNITY} title={'PIC'}>
              <Indemnity
                content={brokerSteps(3)}
                form={FORM_NAME}
                isValidDate={indemnityDateIsValidDate}
              />
            </Tab>
            <Tab eventKey={BANK_ACCOUNTS} title={'Bank Accounts'}>
              <BankAccounts content={brokerSteps(4)} form={FORM_NAME} />
            </Tab>
            <Tab eventKey={REGULATION} title={'Regulation'}>
              <Regulation content={brokerSteps(5)} form={FORM_NAME} />
            </Tab>
            <Tab eventKey={DECLARATION} title={'Declaration'}>
              <Declaration
                content={brokerSteps(6)}
                form={FORM_NAME}
                isValidDate={declarationDateIsValidDate}
              />
            </Tab>
            <Tab eventKey={USERS} title={'Users'}>
              <BrokerApUsers form={FORM_NAME} />
            </Tab>
            <Tab eventKey={INSURERS} title={'Insurers'}>
              <BrokerApplicationCommissions
                availableProducts={availableProducts}
                commissions={formValues?.data.attributes.broker_commission}
              />
              <Box>
                <Row>
                  <Col sm={12}>
                    <h4 className="page-title">Admin Fee</h4>
                  </Col>
                </Row>
                <Row>
                  <Col sm={4}>
                    <Field
                      name={'data.attributes.metadata.admin_fee_tier'}
                      component={SelectMenu}
                      optionValuePath={'attributes.name'}
                      source={'brokers/admin-fees'}
                    />
                  </Col>
                </Row>
              </Box>
            </Tab>
            {hasPermission('communication.view') && brokerApplicationId && (
              <Tab eventKey={EMAILS} title={'Emails'}>
                <BrokerApplicationEmails brokerApplicationId={brokerApplicationId} />
              </Tab>
            )}
          </Tabs>
        </div>
        <div className="btn-bar-container">
          <InternalButtonBar buttons={buttons} />
        </div>
      </Form>
    </InternalContainer>
  );
};

const validOrganisation = (organisation) => {
  if (organisation) {
    return !!organisation.name;
  }
  return false;
};

const formValidForApproval = (formValues) => {
  if (!formValues) {
    return false;
  }

  const organisation =
    get(formValues, 'data.relationships.organisation', false) &&
    get(formValues, 'data.relationships.organisation.data', false) &&
    get(formValues, 'data.relationships.organisation.data.attributes', false)
      ? get(formValues, 'data.relationships.organisation.data.attributes')
      : get(formValues, 'data.attributes.organisation');

  return (
    has(formValues, 'data.attributes.trading_name') &&
    validOrganisation(organisation) &&
    (has(formValues, 'data.relationships.legal_entity_type.data.id') &&
      get(formValues, 'data.relationships.legal_entity_type.data.id') !== '') &&
    (formValues.data.attributes.users && formValues.data.attributes.users.length > 0) &&
    has(formValues, 'data.attributes.users[0].email') &&
    get(formValues, 'data.attributes.metadata.declaration.broker_declaration', false)
  );
};

// dispatch(change('data.attributes.metadata.generate_documents', true));
// dispatch(change('data.attributes.metadata.productId', productId));
const getInitialValues = (resource, insurers) => () => {
  const brokerCommission = resource.data.attributes.broker_commission;
  const commissions = getAssignedCommissions(brokerCommission);

  return {
    ...resource,
    data: {
      ...resource.data,
      attributes: {
        ...resource.data.attributes,
        broker_commission: getFormattedInsurers(commissions, insurers),
        metadata: {
          ...resource.data.attributes.metadata,
          generate_documents: true,
        },
      },
    },
  };
};

export default compose(
  connect((state, ownProps) => {
    return {
      initialValues: getInitialValues(ownProps.resource, ownProps.insurers)(state),
      formValues: getFormValues(FORM_NAME)(state),
    };
  }),

  reduxForm({
    form: FORM_NAME,
  }),
)(BrokerApplicationForm);
